import { useAuth, useClerk, useUser } from '@clerk/react-router';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { readProviderNameHint, readSignedInHint, writeSignedInHint } from '~/routing/signedInHint';

/**
 * What the PUBLIC header knows about the visitor. [MCV-1723]
 *
 * Public pages render without Clerk on the primary host, so the header cannot
 * call Clerk hooks itself. It reads this context instead: Clerk-free trees get
 * the default (`signed-out`) with no provider, and a tree under ClerkProvider
 * gets the real answer from ClerkPublicAuthBridge.
 *
 * `loading` exists only to avoid flashing "Log in" at a visitor who is probably
 * signed in; the header renders nothing in that slot until Clerk answers.
 */
export type PublicAuthState =
  | { status: 'signed-out' }
  | { status: 'loading' }
  | { status: 'signed-in'; displayName: string | null; imageUrl: string | null };

const SIGNED_OUT: PublicAuthState = { status: 'signed-out' };
const LOADING: PublicAuthState = { status: 'loading' };

/**
 * How long a hinted visitor waits for Clerk before the header falls back to
 * "Log in". A clerk-js load that HANGS (stalled CDN, blocked network) never sets
 * `isLoaded` or `status: 'error'`, and these pages are Clerk-free in the first
 * place for resilience when auth is degraded, so `loading` must not be a dead end.
 */
export const CLERK_ANSWER_TIMEOUT_MS = 5000;

export const PublicAuthContext = createContext<PublicAuthState>(SIGNED_OUT);

export const usePublicAuth = (): PublicAuthState => useContext(PublicAuthContext);

/**
 * Feeds PublicAuthContext from Clerk, and keeps the signed-in hint
 * (routing/signedInHint.ts) honest. Only ever render this inside ClerkProvider.
 */
export const ClerkPublicAuthBridge = ({ children }: PropsWithChildren) => {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  // Same signal as <ClerkFailed>, which also reads `status` from this context.
  const clerkStatus = useClerk().status;
  // Read once. "Was this browser probably signed in when the page opened" is
  // what decides between `loading` and `signed-out` while Clerk boots. On the
  // HLX host Clerk always mounts, so an anonymous visitor has no hint and keeps
  // seeing "Log in" immediately, exactly as before.
  const [hadHintAtMount] = useState(readSignedInHint);
  const [gaveUpWaiting, setGaveUpWaiting] = useState(false);

  // Bounds `loading` (see CLERK_ANSWER_TIMEOUT_MS). If Clerk answers later the
  // state still corrects to the real one, so the slow case costs a brief "Log in".
  useEffect(() => {
    if (isLoaded || !hadHintAtMount) return undefined;
    const timer = window.setTimeout(() => setGaveUpWaiting(true), CLERK_ANSWER_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isLoaded, hadHintAtMount]);

  // Establishes the hint after login and clears it after sign-out or session
  // expiry, on any page under Clerk (application or public).
  useEffect(() => {
    if (isLoaded) writeSignedInHint(Boolean(isSignedIn));
  }, [isLoaded, isSignedIn]);

  // [MCV-1834] A provider's name is the one on their MCV profile, which the app leaves here for
  // this page; the Clerk name is the fallback (employers, or a browser the app never wrote it to).
  // Read on every render, not memoised: the app can write it between two renders of this bridge.
  const profileFirstName = readProviderNameHint(user?.id)?.firstName.trim() || null;
  const firstName = profileFirstName ?? user?.firstName ?? null;
  const fullName = user?.fullName ?? null;
  const imageUrl = user?.hasImage ? (user.imageUrl ?? null) : null;

  const value = useMemo<PublicAuthState>(() => {
    if (isLoaded) {
      if (!isSignedIn) return SIGNED_OUT;
      return { status: 'signed-in', displayName: firstName || fullName || null, imageUrl };
    }
    // A failed or hung Clerk load may never answer; do not leave the slot empty.
    if (clerkStatus === 'error' || gaveUpWaiting) return SIGNED_OUT;
    return hadHintAtMount ? LOADING : SIGNED_OUT;
  }, [
    isLoaded,
    isSignedIn,
    clerkStatus,
    hadHintAtMount,
    gaveUpWaiting,
    firstName,
    fullName,
    imageUrl,
  ]);

  return <PublicAuthContext.Provider value={value}>{children}</PublicAuthContext.Provider>;
};
